import { ref, computed, watch } from 'vue'
import produtoService from '@/services/produtoService'
import categoriaService from '@/services/categoriaService'

export function useProdutos() {
  const produtos = ref([])
  const categorias = ref([])
  const categoriasPorId = ref({}) // Mapeamento para busca rápida por ID
  const loading = ref(false)
  const loadingMore = ref(false) // Para o botão "Ver mais"
  const error = ref(null)
  
  // Estado da paginação
  const paginacao = ref({
    total: 0,
    offset: 0,
    limit: 5,
    hasNext: false,
    hasPrevious: false,
    currentPage: 1,
    totalPages: 0
  })
  
  // Filtros reativos
  const filtros = ref({
    busca: '',
    categoria: null,
    status: null
  })

  // Computed para controlar a visibilidade do botão "Ver mais"
  const hasMoreProducts = computed(() => paginacao.value.hasNext)

  // Computed para verificar se há filtros ativos
  const temFiltros = computed(() => {
    return filtros.value.busca || 
           filtros.value.categoria !== null || 
           filtros.value.status !== null
  })

  /**
   * Carrega a lista inicial de produtos ou reseta a lista ao aplicar filtros.
   * @param {number} offset - Posição inicial (0-based)
   * @param {number} limit - Número de itens por página
   * @param {boolean} fetchAll - Se true, busca um número grande de itens (para vitrine)
   */
  const carregarProdutos = async (offset = 0, limit = paginacao.value.limit, fetchAll = false) => {
    window.scrollTo(0, 0);
    try {
      loading.value = true
      error.value = null

      let response
      const fetchLimit = fetchAll || temFiltros.value ? 9999 : limit;
      
      if (temFiltros.value) {
        // Usar endpoint de filtro quando há filtros
        const filtrosAPI = {
          offset: 0, // Sempre reseta para a primeira página
          limit: fetchLimit
        }
        
        if (filtros.value.busca) {
          filtrosAPI.name = filtros.value.busca
          filtrosAPI.description = filtros.value.busca
        }
        
        if (filtros.value.categoria) {
          filtrosAPI.categoryId = filtros.value.categoria
        }
        
        if (filtros.value.status !== null && filtros.value.status !== undefined) {
          // Converter para booleano se necessário e inverter para API
          const statusBoolean = typeof filtros.value.status === 'boolean' 
            ? filtros.value.status 
            : filtros.value.status === 'true' || filtros.value.status === true
          filtrosAPI.disabled = !statusBoolean
        }
        
        response = await produtoService.filtrar(filtrosAPI)
      } else {
        // Usar endpoint de listagem quando não há filtros
        response = await produtoService.listar({ offset, limit: fetchLimit })
      }

      if (response?.data) {
        // Verificar se a resposta tem a nova estrutura paginada
        if (response.data.items && Array.isArray(response.data.items)) {
          // Nova estrutura paginada
          produtos.value = produtoService.mapearParaFrontend(response.data.items)
          
          // Atualizar estado da paginação
          paginacao.value = {
            total: parseInt(response.data.total || 0, 10),
            offset: parseInt(response.data.offset || 0, 10),
            limit: parseInt(response.data.limit || limit, 10),
            hasNext: response.data.hasNext || false,
            hasPrevious: response.data.hasPrevious || false,
            currentPage: Math.floor((response.data.offset || 0) / (response.data.limit || limit)) + 1,
            totalPages: Math.ceil((response.data.total || 0) / (response.data.limit || limit))
          }
        }
        // Fallback para estrutura antiga (array direto)
        else if (Array.isArray(response.data)) {
          produtos.value = produtoService.mapearParaFrontend(response.data)
          // Definir paginação padrão para estrutura antiga
          paginacao.value = {
            total: response.data.length,
            offset: 0,
            limit: response.data.length,
            hasNext: false,
            hasPrevious: false,
            currentPage: 1,
            totalPages: 1
          }
        }
        // Se tem propriedade data ou similar
        else if (response.data.data && Array.isArray(response.data.data)) {
          produtos.value = produtoService.mapearParaFrontend(response.data.data)
          // Definir paginação padrão
          paginacao.value = {
            total: response.data.data.length,
            offset: 0,
            limit: response.data.data.length,
            hasNext: false,
            hasPrevious: false,
            currentPage: 1,
            totalPages: 1
          }
        }
        // Fallback para mock se API não retorna formato esperado
        else {
          console.warn('Formato de resposta da API não reconhecido, usando mock')
          const mockData = getMockProdutos()
          produtos.value = mockData
          paginacao.value = {
            total: mockData.length,
            offset: 0,
            limit: mockData.length,
            hasNext: false,
            hasPrevious: false,
            currentPage: 1,
            totalPages: 1
          }
        }
      } else {
        console.warn('Resposta da API inválida, usando mock')
        const mockData = getMockProdutos()
        produtos.value = mockData
        paginacao.value = {
          total: mockData.length,
          offset: 0,
          limit: mockData.length,
          hasNext: false,
          hasPrevious: false,
          currentPage: 1,
          totalPages: 1
        }
      }

    } catch (err) {
      console.error('Erro ao carregar produtos:', err.message)
      error.value = err.message
      
      // Fallback para dados mock em caso de erro
      const mockData = getMockProdutos()
      produtos.value = mockData
      paginacao.value = {
        total: mockData.length,
        offset: 0,
        limit: mockData.length,
        hasNext: false,
        hasPrevious: false,
        currentPage: 1,
        totalPages: 1
      }
    } finally {
      loading.value = false
    }
  }


  /**
   * Carrega lista de categorias para filtros
   */
  const carregarCategorias = async () => {
    try {
      const response = await categoriaService.listar({ offset: 0, limit: 100 })
      
      if (response?.data) {
        let categoriasData = []
        
        if (Array.isArray(response.data)) {
          categoriasData = response.data
        } else if (response.data.items) {
          categoriasData = response.data.items
        } else if (response.data.data) {
          categoriasData = response.data.data
        }

        // Mapear categorias para formato esperado pelo select E criar mapeamento por ID
        const categoriasFormatadas = categoriaService.mapearParaFrontend(categoriasData)
        
        categorias.value = categoriasFormatadas.map(cat => ({
          id: cat.id,
          nome: cat.name,
          title: cat.name,
          value: cat.id,
          tagBackgroundColor: cat.tagBackgroundColor,
          tagForegroundColor: cat.tagForegroundColor,
          disabled: cat.disabled
        }))

        // Criar mapeamento por ID para busca rápida
        categoriasPorId.value = {}
        categoriasFormatadas.forEach(cat => {
          categoriasPorId.value[cat.id] = {
            id: cat.id,
            name: cat.name,
            tagBackgroundColor: cat.tagBackgroundColor,
            tagForegroundColor: cat.tagForegroundColor,
            disabled: cat.disabled
          }
        })
      } else {
        // Fallback para categorias mock
        const mockCategorias = getMockCategorias()
        categorias.value = mockCategorias
        
        // Criar mapeamento por ID para o mock também
        categoriasPorId.value = {}
        mockCategorias.forEach(cat => {
          categoriasPorId.value[cat.id] = {
            id: cat.id,
            name: cat.nome,
            tagBackgroundColor: cat.tagBackgroundColor,
            tagForegroundColor: cat.tagForegroundColor,
            disabled:cat.disabled
          }
        })
      }
    } catch (err) {

      console.error('Erro ao carregar categorias:', err.message)
      const mockCategorias = getMockCategorias()
      categorias.value = mockCategorias
      
      // Criar mapeamento por ID para o mock também
      categoriasPorId.value = {}
      mockCategorias.forEach(cat => {
        categoriasPorId.value[cat.id] = {
          id: cat.id,
          name: cat.nome,
          tagBackgroundColor: cat.tagBackgroundColor,
          tagForegroundColor: cat.tagForegroundColor,
          disabled:cat.disabled
        }
      })
    }
  }

  /**
   * Navegação da paginação
   */
  const irParaPagina = async (numeroPagina) => {
    const offset = (numeroPagina - 1) * paginacao.value.limit
    await carregarProdutos(offset, paginacao.value.limit)
  }

  const alterarTamanhoPagina = async (novoLimit) => {
    paginacao.value.limit = novoLimit
    // Ao mudar o limite, sempre voltamos para a primeira página
    await carregarProdutos(0, novoLimit)
  }


  /**
   * Exclui um produto
   */
  const excluirProduto = async (produto) => {
    try {
      await produtoService.excluir(produto.id)
      
      // Remover da lista local
      const index = produtos.value.findIndex(p => p.id === produto.id)
      if (index > -1) {
        produtos.value.splice(index, 1)
        // Atualizar contador total
        paginacao.value.total = Math.max(0, paginacao.value.total - 1)
        paginacao.value.totalPages = Math.ceil(paginacao.value.total / paginacao.value.limit)
      }
      window.location.reload();
      return true
    } catch (err) {
      console.error('Erro ao excluir produto:', err.message)
      error.value = err.message
      return false
    }
  }

  // Watcher para recarregar quando filtros mudarem (volta para primeira página)
  watch(filtros, () => {
    // Debounce para evitar muitas chamadas durante a digitação
    setTimeout(() => {
      carregarProdutos() // Chama sem parâmetros para usar o limite alto padrão
    }, 300)
  }, { deep: true })

  /**
   * Mock data para fallback
   */
  const getMockProdutos = () => [
    {
      id: 1,
      nome: 'Caixa D\'Água Tigre',
      descricao: 'Reservatório de água potável para uso residencial e comercial.',
      categoria: 'Categoria 1',
      categoriaId: 1,
      categorias: [1, 2],
      associatedCategories: [
        { productId: 1, categoryId: 1 },
        { productId: 1, categoryId: 2 }
      ],
      ativo: true,
      status: true,
      temManual: false,
      manuais: [],
      associatedManuals: [],
      imagemMobile: 'product-images/product_mobile.png',
      imagemDesktop: 'product-images/product_desktop.png',
      imagem: 'product-images/product_mobile.png'
    },
    {
      id: 14,
      nome: 'Linha Condulete',
      descricao: 'Caixas e acessórios para instalações elétricas.',
      categoria: '',
      categoriaId: 0,
      categorias: [],
      associatedCategories: [],
      ativo: true,
      status: true,
      temManual: false,
      manuais: [],
      associatedManuals: [],
      imagemMobile: 'product-images/product_mobile.png',
      imagemDesktop: 'product-images/product_desktop.png',
      imagem: 'product-images/product_mobile.png'
    },
    {
      id: 20,
      nome: 'Linha Caixa Sifonada',
      descricao: 'Caixas sifonadas para escoamento de águas.',
      categoria: '',
      categoriaId: 0,
      categorias: [],
      associatedCategories: [],
      ativo: true,
      status: true,
      temManual: false,
      manuais: [],
      associatedManuals: [],
      imagemMobile: 'product-images/product_mobile.png',
      imagemDesktop: 'product-images/product_desktop.png',
      imagem: 'product-images/product_mobile.png'
    }
  ]

  const getMockCategorias = () => [
    { 
      id: 1, 
      nome: 'Eletrônicos', 
      title: 'Eletrônicos', 
      value: 1,
      tagBackgroundColor: '#1976d2',
      tagForegroundColor: '#ffffff',
      disabled: false
    },
    { 
      id: 2, 
      nome: 'Eletrodomésticos', 
      title: 'Eletrodomésticos', 
      value: 2,
      tagBackgroundColor: '#388e3c',
      tagForegroundColor: '#ffffff',
      disabled: true
    },
    { 
      id: 3, 
      nome: 'Móveis', 
      title: 'Móveis', 
      value: 3,
      tagBackgroundColor: '#f57c00',
      tagForegroundColor: '#ffffff',
      disabled: true
    }
  ]

  return {
    produtos,
    categorias,
    categoriasPorId,
    loading,
    loadingMore,
    hasMoreProducts,
    error,
    filtros,
    paginacao,
    temFiltros,
    carregarProdutos,
    carregarCategorias,
    excluirProduto,
    irParaPagina,
    alterarTamanhoPagina
  }
}

export default useProdutos
